import { logger } from '../utils/logger';
import apiClient from './apiClient';

interface PushSubscriptionData {
  endpoint: string;
  expirationTime?: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface PushNotificationPreferences {
  enabled?: boolean;
  meetup_invites?: boolean;
  meetup_updates?: boolean;
  meetup_reminders?: boolean;
  friend_requests?: boolean;
  marketing?: boolean;
  [key: string]: unknown;
}

class PushNotificationService {
  private vapidPublicKey: string | null = null;
  private registration: ServiceWorkerRegistration | null = null;

  /**
   * Initialize the push notification service
   */
  async initialize(): Promise<boolean> {
    try {
      // Check if browser supports notifications
      if (!('serviceWorker' in navigator)) {
        console.warn('Service workers not supported');
        return false;
      }

      if (!('PushManager' in window)) {
        console.warn('Push notifications not supported');
        return false;
      }

      // Register service worker
      this.registration = await navigator.serviceWorker.register('/service-worker.js');
      logger.debug('Service worker registered:', this.registration);

      // Get VAPID public key from server
      const response = await apiClient.api.get('/v1/push/vapid-public-key');
      this.vapidPublicKey = response.data.key;

      return true;
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      return false;
    }
  }

  /**
   * Check if user has granted notification permission
   */
  getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<NotificationPermission> {
    const permission = await Notification.requestPermission();
    logger.debug('Notification permission:', permission);
    return permission;
  }

  /**
   * Check if user is subscribed to push notifications
   */
  async isSubscribed(): Promise<boolean> {
    if (!this.registration) {
      await this.initialize();
    }

    if (!this.registration) {
      return false;
    }

    const subscription = await this.registration.pushManager.getSubscription();
    return subscription !== null;
  }

  /**
   * Subscribe user to push notifications
   */
  async subscribe(): Promise<PushSubscriptionData | null> {
    // Initialize if needed
    if (!this.registration || !this.vapidPublicKey) {
      const initialized = await this.initialize();
      if (!initialized) {
        throw new Error('Failed to initialize push notifications');
      }
    }

    // Request permission if needed
    if (this.getPermissionStatus() !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
    }

    // Check if already subscribed
    let subscription = await this.registration!.pushManager.getSubscription();

    // Subscribe if not already
    if (!subscription) {
      const convertedKey = this.urlBase64ToUint8Array(this.vapidPublicKey!);
      subscription = await this.registration!.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });
    }

    // Send subscription to backend
    await this.sendSubscriptionToServer(subscription);

    return subscription.toJSON() as PushSubscriptionData;
  }

  /**
   * Unsubscribe user from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    try {
      if (!this.registration) {
        return false;
      }

      const subscription = await this.registration.pushManager.getSubscription();
      if (!subscription) {
        return false;
      }

      // Unsubscribe from push manager
      const success = await subscription.unsubscribe();

      if (success) {
        // Remove subscription from backend
        await apiClient.api.post('/v1/push/unsubscribe', {
          endpoint: subscription.endpoint
        });
      }

      return success;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  /**
   * Send subscription to backend
   */
  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    await apiClient.api.post('/v1/push/subscribe', subscription);
  }

  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray as Uint8Array<ArrayBuffer>;
  }

  /**
   * Test push notification (for development)
   */
  async testNotification(): Promise<void> {
    await apiClient.api.post('/v1/push/test');
    logger.debug('Test notification sent');
  }

  /**
   * Check if push notifications are supported by the browser
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /**
   * Get user's push notification preferences
   */
  async getPreferences(): Promise<PushNotificationPreferences | null> {
    const response = await apiClient.api.get('/v1/push/preferences');
    return response.data?.data ?? response.data ?? null;
  }

  /**
   * Update user's push notification preferences
   */
  async updatePreferences(preferences: Partial<PushNotificationPreferences>): Promise<void> {
    await apiClient.api.put('/v1/push/preferences', preferences);
    logger.debug('Push notification preferences updated');
  }

  /**
   * Send a test notification (alias for testNotification for compatibility)
   */
  async sendTestNotification(): Promise<void> {
    return this.testNotification();
  }
}

export default new PushNotificationService();
